import { CrownOutlined, DownloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { UserDoc } from '@gooduncles/gu-app-schema';
import { NewValueParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button, message, notification } from 'antd';
import { get } from 'lodash-es';
import { FC, useCallback, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { formatDateForAgCell, loadGridState, onValueSetterWithValidation } from 'src/utils/ag-grid-util';
import { parseIfObject } from 'src/utils/common';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import { AuditLogger } from 'src/utils/audit-logger';

import { StaffUserDoc } from 'src/hooks/subscription/useStaffUsers';

import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';

import classes from './StaffTable.module.scss';

const firebaseManager = FirebaseManager.getInstance();
const auditLogger = AuditLogger.getInstance();
const logName = '직원 관리';
const TABLE_ID = 'staffTable';

const defaultColDef: ColDef<StaffUserDoc> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const UserRoleCellRenderer = (props: any) => {
  const role: UserDoc['role'] = props.value;
  if (!role) return null;
  return (
    <span className={classes[role]}>
      {role === 'admin' ? <CrownOutlined /> : role === 'manager' ? <SafetyOutlined /> : ''}
      {role}
    </span>
  );
};

const onCellValueChangedWithUpdate = async <T extends UserDoc>(
  params: NewValueParams<T>,
  collection: string,
  docId?: string,
  paths?: string,
) => {
  const field = params.colDef.field;
  if (!field) {
    message.error('수정할 수 없는 필드정보입니다.');
    return;
  }

  if (!docId) {
    message.error('id값이 없습니다.');
    return;
  }

  const newValue = get(params.data, field);
  if (newValue !== params.oldValue) {
    try {
      await firebaseManager.updateDoc(collection, docId, { [paths ?? field]: newValue }, { audit: { suppress: true } });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: docId,
        summary: `${logName} - ${collection}/${docId}.${field} 변경`,
        changes: [{ field: paths ?? field, before: params.oldValue, after: newValue }],
      });
      message.success('변경완료');
    } catch (error: any) {
      console.error(error);
      message.error(error?.message ?? '알 수 없는 에러 발생!');
      auditLogger.log({
        action: 'UPDATE',
        entity: 'user',
        entityId: docId,
        summary: `${logName} - ${collection}/${docId}.${field}:: ${parseIfObject(error)}`,
        level: 'error',
      });
    }
  }
};

const StaffTable: FC<{ rowData: StaffUserDoc[] }> = ({ rowData }) => {
  const { user } = useAuth();
  const [gridRef, setGridRef] = useState<AgGridReact<StaffUserDoc> | null>(null);

  const columnDefs: ColDef<StaffUserDoc>[] = useMemo(
    () => [
      { headerName: 'UserId', field: '_id', width: 300, hide: true },
      {
        headerName: '파트너스',
        field: 'partnersUser',
        width: 110,
        cellRenderer: (params: { value: boolean }) => (params.value ? '🚚' : null),
      },
      {
        headerName: '*이름',
        field: 'userName',
        width: 120,
        editable: true,
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
        onCellValueChanged: (params) => onCellValueChangedWithUpdate(params, 'user', params.data._id, 'userName'),
      },
      {
        headerName: '권한',
        field: 'role',
        enableRowGroup: true,
        width: 116,
        cellRenderer: UserRoleCellRenderer,
      },
      {
        headerName: '이메일',
        field: 'email',
        width: 240,
      },
      {
        headerName: '*전화번호',
        field: 'userTel',
        width: 180,
        editable: true,
        cellEditor: 'agTextCellEditor',
        valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
        onCellValueChanged: (params) => onCellValueChangedWithUpdate(params, 'user', params.data._id, 'userTel'),
      },
      {
        headerName: '생성일',
        field: '_timeCreate',
        width: 200,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
      },
    ],
    [],
  );

  const excelExport = useCallback(() => {
    const columns = gridRef?.columnApi.getColumns();
    if (!gridRef || !columns) {
      notification.error({ message: '파일이 준비되지 않았습니다.' });
      auditLogger.log({
        action: 'SYNC',
        entity: 'user',
        entityId: '',
        summary: `${logName} - Excel export 실패`,
        level: 'error',
      });
      return;
    }

    const columnsToExport = columns.map((column) => column.getColId());
    gridRef?.api.exportDataAsExcel({
      author: 'gooduncles',
      sheetName: '직원 관리',
      columnKeys: columnsToExport,
    });
    auditLogger.log({
      action: 'SYNC',
      entity: 'user',
      entityId: '',
      summary: `${logName} - Excel export`,
    });
  }, [gridRef]);

  return (
    <div className='ag-theme-alpine height100'>
      <div className={classes.tableButtons}>
        <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
        <Button type='primary' onClick={excelExport} icon={<DownloadOutlined />}>
          Excel
        </Button>
      </div>
      <AgGridReact
        className={classes.tableContainer}
        ref={setGridRef}
        onGridReady={() => loadGridState(gridRef, TABLE_ID)}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data._id}
        context={{ user }}
        suppressScrollOnNewData={true}
        alwaysShowHorizontalScroll={true}
        animateRows={true}
        rowGroupPanelShow={'always'}
      />
    </div>
  );
};

export default StaffTable;
